import { useArchiveMediaFolderMutation } from "@api/media-folders";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { track } from "@/features/analytics";
import type { IMediaFolder } from "@/shared/types";
import { MediaLibDialogBase } from "../../media-lib-dialog-base";

const DIALOG_ID = "media-lib-folder-archive";

interface IProps {
  folderData: IMediaFolder;
  archiveFilesInFolders?: (folderIds: string[]) => void;
}

export const MediaLibFolderArchiveDialog = ({
  folderData,
  archiveFilesInFolders,
}: IProps) => {
  const dispatch = useAppDispatch();

  const [archiveFolder, { isLoading }] = useArchiveMediaFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    try {
      const resp = await archiveFolder({
        id: folderData._id,
      }).unwrap();
      if (resp && archiveFilesInFolders) {
        archiveFilesInFolders(resp.foldersIds);
      }
    } catch (e) {
      track("error archiving media folder", {
        folderData,
        error: e,
      });
      toast.error("Error archiving folder");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Archive folder" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <p className="tw-text-[13px]">
          Are you sure that you want to archive{" "}
          <b className="tw-font-semibold">“{folderData.title}”</b> folder with
           all of its contents?
        </p>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Archiving...
            </>
          ) : (
            "Yes, archive"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFolderArchiveDialog.dialogId = DIALOG_ID;
